import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap, catchError, map, take } from 'rxjs/operators';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { MessageService } from 'primeng/api';
import { Source } from '../../Sources/models/source.model';
import { SourceApiService } from '../../../api/source/source.api.service';
import { SourceApi } from '@api/source/models/source-api.model';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';

@Injectable()
export class SourceService extends AllResourceAbstractService<Source, SourceApi, Source, SourceApi> {

    constructor(
        private sourceApiService: SourceApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<SourceApi, SourceApi> {
        return this.sourceApiService;
    }

    constructResourceModel(apiModel: SourceApi): Source {
        return new Source(apiModel);
    }

    constructListModel(apiModel: SourceApi): Source {
        return new Source(apiModel);
    }

    create(source: Source): Observable<number> {
        return this.createResource(source).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    createBulk(sources: Source[]): Observable<number[]> {
        return super.createBulk(
            sources,
            {
                actionType: ResourceActionTypes.CREATE
            }
        ).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    update(source: Source): Observable<number> {
        return this.updateResource(source, { actionType: ResourceActionTypes.UPDATE}).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    deleteBulk(sources: Source[]): Observable<void | number[]> {
        const action = {
            actionType: ResourceActionTypes.DELETE,
            count: sources.length
        };
        return this.bulkDeleteResource(sources, action).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    override bulkDeleteResource(
        resources: Source[],
        action: ResourceAction = {actionType: ResourceActionTypes.DELETE},
        broadcast: boolean = true
    ): Observable<number[]> {
        return this.getApiService().deleteByIds(resources.map(x => x.id)).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                return of(null);
            })
        );
    }

    override getById(id: number): Observable<Source> {
        return this.all$.pipe(
            map((sources: Source[]) => {
                return sources?.find(source => source.id === id);
            }),
            take(1)
        );
    };

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as Source;

        // Skip case for bulk create source groups
        if (!resource) {
            return;
        }

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Source.createErrorHeading',
                        detail: this.localizationService.transform('Source.createErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Source.createSucessHeading',
                        detail: this.localizationService.transform('Source.createSucessMessage', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    let detail = this.localizationService.transform('Source.updateErrorMessage', {
                        name: resource.name
                    });

                    if (error.code === CommonApiErrorEnum.duplicateName) {
                        detail = 'Source.updateErrorMessage.duplicate';
                    }

                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Source.updateErrorHeading',
                        detail: detail
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Source.updateSucessHeading',
                        detail: this.localizationService.transform('Source.updateSucessMessage', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Source.deleteErrorHeading',
                        detail: 'Source.deleteErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Source.deleteSucessHeading',
                        detail: 'Source.deleteSucessMessage'
                    });
                }
                break;
            }
        }
    }
}
